import { Injectable } from '@angular/core'
import { environment } from '../../environment'
import { HttpClient } from '@angular/common/http'
import { Observable } from 'rxjs'
import {
  ApiAlert,
  ApiClass,
  ApiDocument,
  ApiLeaveRequest,
  ApiRegisterUser,
  ApiResult,
  ApiRiderAssessment,
  ApiSessionEquipment,
  ApiSessionHorse,
  ApiSessionPairing,
  ApiSessionRider,
  ApiSessionVolunteer,
  ApiUpdateRiderAssessmentData,
  ApiUpdateSessionPairingsData,
  ApiUpdateVolunteerAttendance,
  ApiUpdateVolunteerAttendanceData,
  ApiUser,
  ApiVenueTimeslot
} from '../modal/api.modal'

type HttpOptions = Parameters<HttpClient['get']>[1]

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private apiUrl = environment.apiUrl

  constructor(private http: HttpClient) {}

  get<T>(url: string, options?: HttpOptions): Observable<T> {
    return this.http.get<T>(this.apiUrl + url, options)
  }

  post<T>(url: string, data: any | null, options?: HttpOptions): Observable<T> {
    return this.http.post<T>(this.apiUrl + url, data, options)
  }

  login(email: string, password: string) {
    type LoginResult = ApiResult<{ token: string; volunteer: ApiUser }>
    return this.post<LoginResult>('/volunteer/login', { email, password })
  }

  sessions(start?: string, end?: string) {
    return this.post<ApiResult<{ sessions: ApiClass[] }>>(
      '/volunteer/sessions',
      {
        start_date: start,
        end_date: end
      }
    )
  }

  sessionRiders(ssId: number) {
    return this.get<ApiResult<{ riders: ApiSessionRider[] }>>(
      `/volunteer/session/${ssId}/riders`
    )
  }

  sessionVolunteers(ssId: number) {
    return this.get<ApiResult<{ volunteers: ApiSessionVolunteer[] }>>(
      `/volunteer/session/${ssId}/volunteers`
    )
  }

  sessionEquipments(ssId: number) {
    return this.get<ApiResult<{ equipments: ApiSessionEquipment[] }>>(
      `/volunteer/session/${ssId}/equipments`
    )
  }
  sessionHorses(ssId: number) {
    return this.get<ApiResult<{ horses: ApiSessionHorse[] }>>(
      `/volunteer/session/${ssId}/horses`
    )
  }

  updateVolunteersAttendance(data: ApiUpdateVolunteerAttendanceData) {
    return this.post<
      ApiResult<{ attendances: ApiUpdateVolunteerAttendance[] }>
    >(`/volunteer/updateAttendances`, data)
  }

  getRiderAssessment(ssId: number, riderId: number) {
    return this.get<ApiResult<{ assessment: ApiRiderAssessment }>>(
      `/volunteer/session/${ssId}/rider/${riderId}/assessment`
    )
  }

  updateRiderAssessment(data: ApiUpdateRiderAssessmentData) {
    return this.post<ApiResult<{ assessment: ApiRiderAssessment }>>(
      '/volunteer/updateRiderAssessment',
      data
    )
  }

  sessionParings(ssId: number) {
    return this.get<ApiResult<{ pairings: ApiSessionPairing[] }>>(
      `/volunteer/session/${ssId}/pairings `
    )
  }

  updatePairings(data: ApiUpdateSessionPairingsData) {
    return this.post<ApiResult<{ pairings: ApiSessionPairing[] }>>(
      '/volunteer/updatePairing',
      data
    )
  }

  getLeaveRequests() {
    return this.get<ApiResult<{ leave_requests: ApiLeaveRequest[] }>>(
      '/volunteer/getLeaveRequests'
    )
  }

  addLeaveRequest(type: 'casual' | 'sick', ssId: number) {
    return this.post<ApiResult<{ leave_request: ApiLeaveRequest }>>(
      '/volunteer/addLeaveRequest',
      {
        type,
        session_id: ssId
      }
    )
  }

  changePassword(currentPwd: string, newPwd: string, confirmPwd: string) {
    return this.post<ApiResult<{}>>('/volunteer/changePassword', {
      current_password: currentPwd,
      new_password: newPwd,
      new_password_confirmation: confirmPwd
    })
  }

  getVolunteerTimeslots() {
    return this.get<ApiResult<{ timeslots: ApiVenueTimeslot[] }>>(
      '/volunteer/getVolunteerTimeslots'
    )
  }

  getPreferredTimeslots() {
    return this.get<ApiResult<{ preferred_timeslots: ApiVenueTimeslot[] }>>(
      '/volunteer/getPreferredTimeslots'
    )
  }

  updatePreferredTimeslots(timeslotIds: number[]) {
    return this.post<ApiResult<{}>>('/volunteer/updatePreferredTimeslots', {
      timeslots: timeslotIds
    })
  }

  logout() {
    return this.post<ApiResult<{}>>('/volunteer/logout', null)
  }

  getAlert() {
    return this.get<ApiResult<{ alert: ApiAlert }>>('/volunteer/alert')
  }

  register(user: ApiRegisterUser) {
    return this.post<ApiResult<{ volunteer: ApiUser; token: string }>>(
      '/volunteer/register',
      user
    )
  }

  getDocuments() {
    return this.get<
      ApiResult<{
        data: {
          documents: ApiDocument[]
        }
      }>
    >('/getDocuments')
  }

  sendResetPasswordCode(email: string) {
    return this.post<ApiResult<{}>>('/volunteer/sendPasswordResetCode', {
      email
    })
  }

  resetPassword(
    email: string,
    code: string,
    newPassword: string,
    confirmPassword: string
  ) {
    return this.post<ApiResult<{}>>('/volunteer/resetPassword', {
      email,
      code,
      new_password: newPassword,
      new_password_confirmation: confirmPassword
    })
  }
}
